import { supabase } from './supabase';
import { officeRoamingStorage } from './roamingStorage';

/**
 * Boards the signed-in user can see, with their organisation name.
 *
 * RLS does the scoping — planner.boards is visible only for orgs with Planner
 * enabled, and the board_privacy policy hides private boards the user is not a
 * member of. We deliberately do NOT try to predict which boards will accept a
 * task: creation requires board membership OR planner.tasks.manage, which is
 * not derivable from a client-side read. planner_create_task_from_addin is
 * authoritative, and its refusal is what the user sees.
 *
 * The organisation name is a separate public-schema read: PostgREST cannot
 * embed across schemas, so there is no planner→public join to make here.
 */

// Same cast the main app uses in src/lib/planner/db.ts — the generated types
// only cover the public schema.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const plannerSchema = () => (supabase as any).schema('planner');

export interface BoardOption {
  id: string;
  name: string;
  organization_id: string;
  orgName: string;
}

interface RawBoard {
  id: string;
  name: string;
  organization_id: string;
}

const LAST_BOARD_KEY = 'flowza-addin-last-board';

export async function listBoards(): Promise<{ boards: BoardOption[]; error: string | null }> {
  const { data, error } = await plannerSchema()
    .from('boards')
    .select('id, name, organization_id')
    .eq('is_archived', false)
    .order('name');

  if (error) return { boards: [], error: 'Could not load your boards.' };
  const boards = (data ?? []) as RawBoard[];
  if (boards.length === 0) return { boards: [], error: null };

  const orgIds = [...new Set(boards.map((b) => b.organization_id))];
  const { data: orgs } = await supabase.from('organizations').select('id, name').in('id', orgIds);
  const orgName = new Map(((orgs ?? []) as Array<{ id: string; name: string }>).map((o) => [o.id, o.name]));

  return {
    boards: boards.map((b) => ({ ...b, orgName: orgName.get(b.organization_id) ?? '' })),
    error: null,
  };
}

/** Last board used, so the common case is one tap. Roams with the session. */
export function rememberBoard(boardId: string): void {
  officeRoamingStorage.setItem(LAST_BOARD_KEY, boardId);
}
export function recallBoard(): string | null {
  return officeRoamingStorage.getItem(LAST_BOARD_KEY);
}

/**
 * The board's inbound email address, if it has one and it is enabled.
 *
 * Only used to tell someone where to forward an email whose attachments the
 * add-in cannot carry. Returns null silently when the board has no address —
 * the hint simply degrades to generic advice rather than showing a broken one.
 */
export async function boardEmailAddress(boardId: string, domain: string): Promise<string | null> {
  const { data, error } = await plannerSchema()
    .from('board_email_addresses')
    .select('local_part, enabled')
    .eq('board_id', boardId)
    .maybeSingle();
  if (error || !data?.enabled || !data?.local_part) return null;
  return `${data.local_part}@${domain}`;
}
